import numeral from "numeral";
import React, { useContext, useEffect, useMemo, useState } from 'react';

import ShoppingCartIcon from '@mui/icons-material/ShoppingCart';
import styles from "../styles/components/purchase-tokens.module.scss";

import { Badge, Button, Dialog, DialogActions, DialogContent, Divider, FormControl, TextField } from '@mui/material';
import { AccountContext } from '../context/account';
import tableStyles from "../styles/components/table.module.scss";
import { ModalTitle } from './modal-title';

import { nanoid } from 'nanoid';
import { BulkItem } from '../core/global';
import { tokensModal } from '../data/content';
import { getCostFromTokens } from '../helpers/cart';
import { calculateCustomToken } from "../helpers/tokens";
import { trackEvent } from "../helpers/tracking";
import { WarningMessage } from './error-message';
import { InfoModal } from './info-modal';

interface BulkTokensSelectorProps {
  onChange: (item: BulkItem) => void
}

export const BulkTokensSelector = ({
  onChange
}:BulkTokensSelectorProps) => {

  const {
    bulkTokenPurchase,
  } = useContext(AccountContext);

  const ordered = useMemo(() => {
    return [...bulkTokenPurchase].sort((a, b) => {
      return a.tokens - b.tokens
    })
  }, [bulkTokenPurchase])

  return (
    <div className={styles.bulkTokensSelector}>
      {ordered.map( item => {
        const {name, tokens, discount} = item
        const {total} = getCostFromTokens(item)
        return (
          <div className={styles.item} onClick={() => {
            onChange(item)
          }}>
            <div className={styles.badge}>
              {numeral(discount).format("0,0")}%
            </div>
            <div className={styles.tokens}>
              {numeral(tokens).format("0,0")}
            </div>
            <div className={styles.cost}>
              £{numeral(total).format("0,0.00")}
            </div>
          </div>
        )
      })}
    </div>
  )
}

interface PurchaseTokensModalProps {
  onClose: () => void
  intialCustomTokens?: number
}

export const PurchaseTokensModal = ({
  onClose,
  intialCustomTokens,
}: PurchaseTokensModalProps) => {
  const [selectedBulk, setSelectedBulk] = useState<BulkItem | null>(null)
  const [showTokenModal, setShowTokenModal] = useState(false)

  const {
    account,
    setShowCart,
    updatePurchaseTokens,
    bulkTokenPurchase,
  } = useContext(AccountContext);

  useEffect(() => {
    if (account.uid) {
      trackEvent("manage-address-book-modal", {uid: account.uid})
    }
  }, [account.uid])

  const itemCost = useMemo(() => {
    if (selectedBulk !== null) {
      return getCostFromTokens(selectedBulk)
    }
    return null
  }, [selectedBulk])

  useEffect(() => {
    if (intialCustomTokens && intialCustomTokens > 0) {
      setSelectedBulk(calculateCustomToken(intialCustomTokens, bulkTokenPurchase))
    }
  }, [intialCustomTokens])

  return (
    <Dialog
      open
      onClose={onClose}
      maxWidth={intialCustomTokens ? "sm" : "xs"}
      fullWidth
    >
      <ModalTitle onClose={onClose}>Token purchase</ModalTitle>
      <DialogContent>
        {intialCustomTokens && <WarningMessage message={`Looks like you are ${numeral(intialCustomTokens).format("0,0")} tokens short of making your purchase. Please purchse the shorfall (or more to make best us of our bulk discounts) before you pruchase your data.`} />}
        <h5>Bulk purchase</h5>
        <BulkTokensSelector onChange={(item: BulkItem) => {
          setSelectedBulk(item)
        }} />
        <Divider textAlign='center' sx={{mb: '16px'}}>OR</Divider>
        <FormControl sx={{width: "100%"}}>
          <TextField
            id="custom"
            name="custom"
            label="Custom number of tokens"
            placeholder="Custom number of tokens"
            value={selectedBulk && selectedBulk.name === "Custom" ? Number(selectedBulk.tokens) : ''}
            type='number'
            autoFocus
            fullWidth
            variant="outlined"
            onChange={(event: React.SyntheticEvent) => {
              // @ts-ignore
              const customTokenValue = calculateCustomToken(event.currentTarget.value as string, bulkTokenPurchase)
              setSelectedBulk(customTokenValue)
            }}
            // color={titleError ? 'error' : 'primary'}
            sx={{ ariaLabel: 'title' }}
          />
        </FormControl>
        {!itemCost && 
          <div className={styles.summary}>
            <p>Please select your option.</p>
          </div>
        }
        {itemCost && selectedBulk &&
          <div className={styles.costRow}>
            <h4>Cost</h4>
            <table className={tableStyles.table}>
              <tbody>
                <tr>
                  <th className={tableStyles.fieldName}>Subtotal GBP:</th>
                  <td className={tableStyles.currency}>£{numeral(itemCost.subtotal).format("0,0.00")}</td>
                </tr>
                {selectedBulk && selectedBulk.name === "Custom" && 
                  <tr>
                    <th className={tableStyles.fieldName}>Discount %:</th>
                    <td className={tableStyles.currency}>{numeral(selectedBulk.discount).format("0,0")}%</td>
                  </tr>
                  }
                <tr>
                  <th className={tableStyles.fieldName}>Discount GBP:</th>
                  <td className={tableStyles.currency}>
                    {itemCost.discount === 0 ?
                      0
                    :
                      <>
                        - £{numeral(itemCost.discount).format("0,0.00")}
                      </>
                    }
                    
                    
                  </td>
                </tr>
                <tr>
                  <th className={tableStyles.fieldName}>Total GBP:</th>
                  <td className={tableStyles.currency}>£{numeral(itemCost.total).format("0,0.00")}</td>
                </tr>
                <tr>
                  <th className={tableStyles.fieldName}>
                    <Badge
                      className={styles.badge}
                      badgeContent={"?"} 
                      color="info" 
                      onClick={()=> {
                        setShowTokenModal(true)
                      }}
                    >
                    Purchased tokens:
                    </Badge>
                  </th>
                  <td className={tableStyles.currency}>{numeral(Math.ceil(selectedBulk?.tokens)).format("0,0")}</td>
                </tr>
              </tbody>
            </table>
          </div>
        }
        <InfoModal
          open={showTokenModal}
          title={tokensModal.title}
          content={tokensModal.content}
          maxWidth="sm"
          onClose={() => {
            setShowTokenModal(false)
          }}
        />
      </DialogContent>
      <>
      <DialogActions sx={{justifyContent: 'space-between', pb: 3, px: 3}}>
          <Button onClick={onClose}>Close</Button>
          <Button
            variant="contained"
            color="success"
            endIcon={<ShoppingCartIcon sx={{color: '#fff'}} />}
            disabled
            onClick={async (e: React.SyntheticEvent) => {
              e.preventDefault()
              if (selectedBulk) {
                updatePurchaseTokens( t => {
                  return [
                    ...t,
                    {
                      ...selectedBulk,
                      id: nanoid(),
                    }
                  ]
                })
                setShowCart(true)
                if (account.uid) {
                  trackEvent("add-tokens-to-basket", {uid: account.uid, ...selectedBulk})
                }
              }
              onClose()
              // if (currentLayer && startDate && endDate) {
              //   await addItem({
              //     location: `${lat} ${lon}`,
              //     layer: currentLayer as string,
              //     model: "mow-mike21-europe",
              //     start_date: format(startDate, "MM/dd/yyyy"),
              //     end_date: format(endDate, "MM/dd/yyyy"),
              //     project_id: itemProjectId
              //   })
              //   refetchCart()
              //   setShowCart(true)
              //   setShowPurchaseData(false)
              // }
            }}
          >
            Add to basket
          </Button>
        </DialogActions>
      </>
    </Dialog>

  )
}
